import { createAsyncThunk } from "@reduxjs/toolkit";
import * as API from "../../services/sampleRequestService";
import { Attachment } from "../../models/sampleRequestModel";

export const getSampleRequests = createAsyncThunk(
  "sampleRequest/list",
  async (params: API.SampleRequestListParams, { rejectWithValue }) => {
    try {
      const { data } = await API.getSampleRequests(params);
      return data; // { data, pagination }
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getSampleRequestStatusCounts = createAsyncThunk(
  "sampleRequest/statusCounts",
  async (_: void, { rejectWithValue }) => {
    try {
      const { data } = await API.getSampleRequestStatusCounts();
      return data.data; // counts keyed by tab name
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getSampleRequestById = createAsyncThunk(
  "sampleRequest/getById",
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await API.getSampleRequestById(id);
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const createSampleRequest = createAsyncThunk(
  "sampleRequest/create",
  async (
    payload: {
      requestedBy: string;
      sampleInfo: Record<string, any>;
      deliveryInfo: Record<string, any>;
    },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await API.createSampleRequest(payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateSampleRequest = createAsyncThunk(
  "sampleRequest/update",
  async (
    args: {
      id: string;
      actorName: string;
      sampleInfo?: Record<string, any>;
      deliveryInfo?: Record<string, any>;
    },
    { rejectWithValue },
  ) => {
    try {
      const { id, ...payload } = args;
      const { data } = await API.updateSampleRequest(id, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Every workflow-transition endpoint has the identical "call it, unwrap
// {data}, return the updated document" shape — this factory avoids eight
// copies of the same try/catch, same instinct as notifyOperationsOfAttachment
// on the delivery-attachment backend.
const wrapMutation = <Args>(type: string, fn: (args: Args) => Promise<any>) =>
  createAsyncThunk(type, async (args: Args, { rejectWithValue }) => {
    try {
      const { data } = await fn(args);
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  });

export const approveSampleRequest = wrapMutation<{
  id: string;
  actorName: string;
}>("sampleRequest/approve", ({ id, actorName }) =>
  API.approveSampleRequest(id, actorName),
);

export const rejectSampleRequest = wrapMutation<{
  id: string;
  actorName: string;
  reason: string;
}>("sampleRequest/reject", ({ id, actorName, reason }) =>
  API.rejectSampleRequest(id, actorName, reason),
);

export const requestSampleRequestChanges = wrapMutation<{
  id: string;
  actorName: string;
  remarks: string;
  requestedFor: "Sales" | "China";
}>("sampleRequest/requestChanges", ({ id, actorName, remarks, requestedFor }) =>
  API.requestSampleRequestChanges(id, actorName, remarks, requestedFor),
);

export const submitSampleRequestChanges = wrapMutation<{
  id: string;
  actorName: string;
}>("sampleRequest/submitChanges", ({ id, actorName }) =>
  API.submitSampleRequestChanges(id, actorName),
);

export const submitSampleRequestToFactory = wrapMutation<{
  id: string;
  actorName: string;
  toolingCost: string;
  toolingRequestBy?: string;
  attachments?: { fileName: string; fileUrl: string; uploadedBy?: string }[];
}>(
  "sampleRequest/submitToFactory",
  ({ id, actorName, toolingCost, toolingRequestBy, attachments }) =>
    API.submitSampleRequestToFactory(id, {
      actorName,
      toolingCost,
      toolingRequestBy,
      attachments,
    }),
);

export const approveSampleRequestCAD = wrapMutation<{
  id: string;
  actorName: string;
}>("sampleRequest/approveCAD", ({ id, actorName }) =>
  API.approveSampleRequestCAD(id, actorName),
);

export const submitSampleRequestProduction = wrapMutation<{
  id: string;
  actorName: string;
  courier: string;
  trackingNumber: string;
  shippedDate: string;
  attachments?: Attachment[];
}>(
  "sampleRequest/submitProduction",
  ({ id, actorName, courier, trackingNumber, shippedDate, attachments }) =>
    API.submitSampleRequestProduction(id, actorName, {
      courier,
      trackingNumber,
      shippedDate,
      attachments,
    }),
);

export const confirmSampleRequestReceived = wrapMutation<{
  id: string;
  actorName: string;
}>("sampleRequest/confirmReceived", ({ id, actorName }) =>
  API.confirmSampleRequestReceived(id, actorName),
);

export const cancelSampleRequest = wrapMutation<{
  id: string;
  actorName: string;
  reason?: string;
}>("sampleRequest/cancel", ({ id, actorName, reason }) =>
  API.cancelSampleRequest(id, actorName, reason),
);

// export const updateSampleRequest = createAsyncThunk(
//   "sampleRequest/update",
//   async (
//     payload: {
//       id: string;
//       actorName: string;
//       sampleInfo?: Record<string, any>;
//       deliveryInfo?: Record<string, any>;
//     },
//     { rejectWithValue },
//   ) => {
//     try {
//       const { id, ...body } = payload;
//       const response = await API.patch(`/sample-requests/${id}`, body);
//       return response.data.data;
//     } catch (err: any) {
//       return rejectWithValue(
//         err?.response?.data?.message || "Failed to update the sample request.",
//       );
//     }
//   },
// );
